import type React from "react";
import { useEffect, useState } from "react";
import "./UnscrambleSentences.scss";
import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import ReloadIcon from "@rsuite/icons/Reload";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { ButtonGroup, ButtonToolbar, IconButton } from "rsuite";
import { v4 as uuidv4 } from "uuid";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import { Stepper } from "../../../shared/components/Stepper/Stepper";
import { shuffle } from "../../../shared/helpers";
import { DropArea } from "./components/DropArea";
import { Word } from "./components/Word";

type Props = {
	data: string[];
	activityId: string;
};

const UnscrambleSentences: React.FC<Props> = ({ data, activityId }) => {
	const [startIndex, setStartIndex] = useState<number>(0);
	const [arrayOfWords, setArrayOfWords] = useState<
		{ word: string; index: number }[]
	>([]);
	const [droppedWords, setDroppedWords] = useState<
		{ word: string; originalIndex: number }[]
	>([]);
	const [canPressCheck, setCanPressCheck] = useState<boolean>(false);
	const [fireworksTrigger, setFireworksTrigger] = useState(false);
	const { triggerActivityCompletion } = useActivityCompletion();
	const prefixClass = "unscramble-activity";
	const [steps, setSteps] = useState(
		data.map((_, index) => {
			return {
				index,
				current: index === startIndex,
				completed: false,
			};
		}),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setSteps(
			steps.map((steps, index) => {
				return {
					...steps,
					current: index === startIndex,
				};
			}),
		);
	}, [startIndex]);

	const initialize = () => {
		const shuffledArrayOfWords =
			shuffle(data[startIndex]?.split(" "))?.map((word) => word) || [];

		const wordsWithIndexes = shuffledArrayOfWords?.map(
			(word, index: number) => ({ word, index, id: uuidv4() }),
		);

		setArrayOfWords(wordsWithIndexes);

		setDroppedWords(
			new Array(data[startIndex].split(" ").length).fill({
				word: "",
				originalIndex: -1,
			}),
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		initialize();
	}, [startIndex]);

	useEffect(() => {
		const pressCheckBtnIsEnabled = droppedWords.every(
			(item) => item.word !== "",
		);
		setCanPressCheck(pressCheckBtnIsEnabled);
	}, [droppedWords]);

	const handleDropWord = (
		dropAreaIndex: number,
		draggedWordObj: { word: string; index: number },
	) => {
		const newDroppedWords = [...droppedWords];
		newDroppedWords[dropAreaIndex] = {
			word: draggedWordObj.word,
			originalIndex: draggedWordObj.index,
		};
		setDroppedWords(newDroppedWords);

		const newArrayOfWords = [...arrayOfWords];
		newArrayOfWords[draggedWordObj.index].word = "";
		setArrayOfWords(newArrayOfWords);
	};

	const handleRemoveWord = (dropAreaIndex: number) => {
		const wordObj = droppedWords[dropAreaIndex];
		const newArrayOfWords = [...arrayOfWords];
		newArrayOfWords[wordObj?.originalIndex].word = wordObj.word;
		setArrayOfWords(newArrayOfWords);

		const newDroppedWords = [...droppedWords];
		newDroppedWords[dropAreaIndex] = { word: "", originalIndex: -1 };
		setDroppedWords(newDroppedWords);
	};

	const checkAnswersHandler = async () => {
		const originalSentence = data[startIndex];
		const unScrambledSentence = droppedWords.map((item) => item.word).join(" ");

		if (unScrambledSentence === originalSentence) {
			setSteps((prev) => {
				const newSteps = [...prev];
				newSteps[startIndex].completed = true;
				return newSteps;
			});

			if (startIndex < data.length - 1) {
				setTimeout(() => {
					setStartIndex((prev) => prev + 1);
				}, 1500);
			} else {
				setTimeout(async () => {
					setFireworksTrigger(true);
					triggerActivityCompletion(activityId, 16, resetActivity);
					console.log("All sentences completed!");
				}, 2000);
			}
		} else {
			alert("Some answers are incorrect. Try again!");
		}
	};

	const nextSentenceHandler = () => {
		if (startIndex < data.length - 1) {
			setStartIndex((prev) => prev + 1);
			setCanPressCheck(false);
		}
	};

	const prevSentenceHandler = () => {
		if (startIndex > 0) {
			setStartIndex((prev) => prev - 1);
			setCanPressCheck(false);
		}
	};

	const resetActivity = () => {
		initialize();
		setStartIndex(0);
		setSteps(
			data.map((_, index) => {
				return {
					index,
					current: index === startIndex,
					completed: false,
				};
			}),
		);
	};

	return (
		<div className={`${prefixClass}__container`}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={() => setFireworksTrigger(false)}
				/>
			)}
			<div className={`${prefixClass}__toolbar`}>
				<ButtonToolbar>
					<IconButton
						onClick={resetActivity}
						appearance="primary"
						icon={<ReloadIcon color="white" />}
					/>
					<IconButton
						disabled={!canPressCheck}
						onClick={checkAnswersHandler}
						appearance="primary"
						icon={<CheckOutlineIcon />}
					>
						Check
					</IconButton>
					<ButtonGroup>
						<IconButton
							onClick={prevSentenceHandler}
							disabled={steps[startIndex - 1]?.completed}
							appearance="primary"
							icon={<IoIosArrowBack />}
						/>
						<IconButton
							onClick={nextSentenceHandler}
							appearance="primary"
							icon={<IoIosArrowForward />}
						/>
					</ButtonGroup>
				</ButtonToolbar>
			</div>
			<Stepper steps={steps} />
			<div className={`${prefixClass}__inner-container`}>
				<div className={`${prefixClass}__scrambled-words`}>
					{arrayOfWords.map((wordObj, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
						<Word arrayOfWords={arrayOfWords} key={index} wordObj={wordObj} />
					))}
				</div>
				<div className={`${prefixClass}__droppable-area`}>
					{droppedWords.map((wordObj, index) => (
						<DropArea
							onDropWord={handleDropWord}
							onRemoveWord={handleRemoveWord}
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							key={index}
							word={wordObj.word}
							droppedWords={droppedWords}
							dropAreaIndex={index}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default UnscrambleSentences;
